/**
 * Tags Field Type - Inline Renderer
 *
 * Read-only in-table cell renderer that displays array values as individual
 * tag chips. Tags is a read-only field type, so the editable alias falls back
 * to the read-only renderer.
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { CellContainer } from '../../tableStyles';
import { TagsWithOverflow } from 'components/tags/TagsWithOverflow';
import type { ColumnRenderer, RenderOptions, CustomColumn } from '../../columnModel';
import { parseTagsValue } from '../tags';

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Tags read-only renderer - displays values as individual tag chips
 * Automatically formats arrays into tag elements with boundaries
 * Includes internal Tooltip for consistent hover behavior
 */
export const tagsReadOnlyRenderer = <T extends { id: number | string }>(
  _col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => ({
  cell: (cellContext: CellContext<T, unknown>) => {
    const value = cellContext.getValue();
    const tags = parseTagsValue(value);

    if (tags.length === 0) {
      return <CellContainer />;
    }

    return (
      <CellContainer>
        <TagsWithOverflow
          values={tags.map((tag, idx) => ({ id: `${tag}-${idx}`, label: tag }))}
          columnWidth={cellContext.column.getSize()}
        />
      </CellContainer>
    );
  },
});

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const tagsReadOnly: ColumnRenderer = tagsReadOnlyRenderer;

/** Editable renderer - tags type is read-only, falls back to read-only */
export const tagsEditable: ColumnRenderer = tagsReadOnlyRenderer;
